'use client';

import * as React from 'react';

import { cn } from '@heidi/utils';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '../../../drawer';
import { useDesignSystemIntl } from '../../../hooks/use-design-system-intl';

import { PanelMobile, PanelProvider } from '../panel';
import { SidebarProvider } from '../sidebar';
import {
  sidebarMobileContentClasses,
  sidebarMobileInnerClasses,
} from '../sidebar/sidebar.styles';
import type { MobileSidebarProps } from './mobile-sidebar.types';

/**
 * Standalone mobile sidebar drawer with panel switching navigation.
 *
 * Use this component when you need a mobile sidebar drawer **outside** of PageShell,
 * such as in a mobile app header. It encapsulates all the necessary providers
 * (SidebarProvider, PanelProvider) and the Vaul drawer.
 *
 * ## Why this has its own SidebarProvider (temporary architecture)
 *
 * This component includes its own SidebarProvider because it's designed to work
 * **independently** of PageShell during the gradual migration to the PageShell slots pattern.
 *
 * During the rollout phase, pages may use MobileSidebar in their mobile header while
 * the desktop layout still uses the legacy sidebar implementation. This means there can
 * temporarily be two SidebarProviders in the component tree:
 * 1. The one inside PageShell (for desktop sidebar)
 * 2. The one inside MobileSidebar (for mobile drawer)
 *
 * This duplication is intentional and temporary. Once all pages fully adopt PageShell
 * with its slots pattern (`<PageShell.Sidebar>`, `<PageShell.Main>`), the mobile sidebar
 * will be integrated into PageShell's responsive behavior and this standalone component
 * can be simplified or deprecated.
 *
 * @example
 * ```tsx
 * <PageShell.MobileSidebar open={isOpen} onOpenChange={setIsOpen}>
 *   <SidebarHeader>...</SidebarHeader>
 *   <SidebarContent>...</SidebarContent>
 *   <SidebarFooter>...</SidebarFooter>
 * </PageShell.MobileSidebar>
 * ```
 */
const MobileSidebar = React.forwardRef<HTMLDivElement, MobileSidebarProps>(
  (
    {
      'data-testid': testId,
      children,
      className,
      open,
      onOpenChange,
      pageId = 'mobile-sidebar',
      title: titleProp,
      description: descriptionProp,
      ...props
    },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const title =
      titleProp ??
      intl.formatMessage({
        defaultMessage: 'Navigation',
        id: '9iD7mhtJwP',
        description: 'Screen-reader title for the mobile sidebar navigation drawer',
      });
    const description =
      descriptionProp ??
      intl.formatMessage({
        defaultMessage: 'Main navigation menu',
        id: '1uJqa+lPqv',
        description: 'Screen-reader description for the mobile sidebar navigation drawer',
      });
    return (
      <SidebarProvider defaultOpen forceMobile className="!min-h-0 !flex-none">
        <Drawer
          open={open}
          onOpenChange={onOpenChange}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- overlay focus management: moves focus into the just-opened drawer
          autoFocus
        >
          <DrawerContent
            ref={ref}
            data-testid={testId}
            data-sidebar="sidebar"
            data-slot="sidebar"
            data-mobile="true"
            className={cn(sidebarMobileContentClasses, className)}
            {...props}
          >
            <DrawerHeader className="sr-only">
              <DrawerTitle>{title}</DrawerTitle>
              <DrawerDescription>{description}</DrawerDescription>
            </DrawerHeader>
            <PanelProvider pageId={pageId}>
              <PanelMobile
                className={sidebarMobileInnerClasses}
                sidebarContent={children}
              />
            </PanelProvider>
          </DrawerContent>
        </Drawer>
      </SidebarProvider>
    );
  }
);
MobileSidebar.displayName = 'PageShell.MobileSidebar';

export { MobileSidebar };
